/**
 * DesktopModal — View (SE-5086)
 *
 * Pure presentation for the shared desktop modal chrome — the desktop sibling
 * of MobileSheetChrome (SE-5081). Renders the Figma "Search Modal" frame
 * (40000732:28566): a centered card with a Heading-2 title top-left, a 44×40 X
 * close button top-right, an optional scrollable body, an optional sticky
 * footer, a hardShadow:5 drop shadow, rounded-lg (12px) corners, and a dimmed
 * scrim backdrop.
 *
 * All logic (ESC handling, backdrop-dismissal gating) lives in
 * DesktopModalContainer. The View receives the already-resolved
 * `onBackdropPress` callback (or undefined when closeOnBackdrop is false) and
 * the resolved pixel `width`.
 *
 * Consumers do NOT pass testIDs — the 5 chrome anchors
 * (TEST_ID_DESKTOP_MODAL.{ROOT,CLOSE,TITLE,BODY,FOOTER}) are hard-coded here so
 * Playwright queries are stable across the SE-5013/5017/5021 consumers.
 * @module components/DesktopModal
 */

import { XIcon } from "lucide-react-native";
import { memo, type ReactNode } from "react";
import type { ViewStyle } from "react-native";

import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalContent,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";

import { TEST_ID_DESKTOP_MODAL } from "@/e2e/testId";

/**
 * Props for DesktopModalView.
 *
 * Internal to the component — consumers use the Container's public
 * DesktopModalProps (re-exported from the barrel).
 */
export interface DesktopModalViewProps {
  /** Whether the modal is open. Drives Modal open state. */
  readonly isOpen: boolean;
  /** Dismissal handler (X tap, backdrop tap, ESC). */
  readonly onClose: () => void;
  /**
   * Backdrop-tap handler, already gated by the Container: `onClose` when
   * closeOnBackdrop is true, `undefined` when false (the "Discard changes?"
   * flow that must not dismiss on an outside click).
   */
  readonly onBackdropPress: (() => void) | undefined;
  /** Whether ESC dismisses the modal — forwarded to the Modal primitive. */
  readonly isKeyboardDismissable: boolean;
  /** Modal title rendered top-left as Heading-2 (Satoshi Bold 18). */
  readonly title: string;
  /**
   * Resolved content style carrying the Figma-pinned pixel width. Computed in
   * the Container (Views use arrow-shorthand and hoist computed values up) so
   * the object identity stays stable across re-renders.
   */
  readonly contentStyle: ViewStyle;
  /** Modal body — rendered inside a vertically-scrolling region. */
  readonly children: ReactNode;
  /**
   * Optional sticky footer slot. Stays anchored to the bottom of the modal;
   * body scrolls behind it. Typical use: a primary CTA button row.
   */
  readonly footer?: ReactNode;
}

/**
 * Caps the modal at 85% of the viewport height so a tall body scrolls inside
 * the card instead of pushing the header + footer off-screen. Mirrors the
 * viewport-height cap MobileSheetChrome uses for the same reason. The
 * arbitrary value is necessary — no `max-h` sizing token is viewport-relative.
 *
 * Exported so unit tests can lock the regression without re-deriving the class.
 */
export const DESKTOP_MODAL_MAX_HEIGHT_CLASS = "max-h-4/5";

/**
 * Pure presentation for the shared desktop modal chrome.
 * @param props - Component props (see DesktopModalViewProps)
 * @param props.isOpen - Whether the modal is currently open
 * @param props.onClose - Dismissal handler bound by the Container
 * @param props.onBackdropPress - Gated backdrop-tap callback (undefined when closeOnBackdrop=false)
 * @param props.isKeyboardDismissable - Whether ESC dismisses (forwarded to Modal)
 * @param props.title - Modal title rendered top-left
 * @param props.contentStyle - Resolved width style (computed in the Container)
 * @param props.children - Modal body rendered inside the scrollable ModalBody
 * @param props.footer - Optional sticky footer node
 */
const DesktopModalView = ({
  isOpen,
  onClose,
  onBackdropPress,
  isKeyboardDismissable,
  title,
  contentStyle,
  children,
  footer,
}: DesktopModalViewProps) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    closeOnOverlayClick={onBackdropPress !== undefined}
    isKeyboardDismissable={isKeyboardDismissable}
    avoidKeyboard
  >
    {/* The Modal atom's backdrop already renders the semantic 50%-opacity
     * scrim (bg-surface-inverse @ 0.5) — no color override needed. */}
    <ModalBackdrop onPress={onBackdropPress} />
    <ModalContent
      testID={TEST_ID_DESKTOP_MODAL.ROOT}
      className={`${DESKTOP_MODAL_MAX_HEIGHT_CLASS} rounded-lg bg-surface-base p-0 shadow-hard-5`}
      // eslint-disable-next-line no-restricted-syntax -- runtime-resolved modal pixel width (custom/preset px off the sizing ladder); ModalContent is a bare re-export atom that takes style directly (no UNSAFE_style prop)
      style={contentStyle}
    >
      {/* Sticky header band — title left, X close right (44×40 per Figma). */}
      <Stack
        direction="horizontal"
        className="items-center justify-between p-6 pb-3"
      >
        {/* testID lives on a wrapping Box — Text/Heading reject `testID` on
         * RN-web (the SeeAllListsRowView / ShareWatchlistModal convention). */}
        <Box testID={TEST_ID_DESKTOP_MODAL.TITLE} className="flex-1">
          <Heading variant="title" className="text-content-primary">
            {title}
          </Heading>
        </Box>
        <Pressable
          testID={TEST_ID_DESKTOP_MODAL.CLOSE}
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          className="h-10 w-11 items-center justify-center rounded-md"
        >
          <Icon as={XIcon} size="md" className="text-content-secondary" />
        </Pressable>
      </Stack>

      {/* Scrolling body — ModalBody is itself a ScrollView (see ui/modal),
       * so the body scrolls inside the height-capped card without a nested
       * ScrollView (which would double-scroll). */}
      <ModalBody
        testID={TEST_ID_DESKTOP_MODAL.BODY}
        className="w-full"
        contentContainerClassName="px-6 pb-6 pt-0"
        keyboardShouldPersistTaps="handled"
      >
        {children}
      </ModalBody>

      {/* Sticky footer — anchored at the bottom; body scrolls behind it. */}
      {footer ? (
        <Box
          testID={TEST_ID_DESKTOP_MODAL.FOOTER}
          className="w-full border-t border-outline-subtle p-6"
        >
          {footer}
        </Box>
      ) : null}
    </ModalContent>
  </Modal>
);

DesktopModalView.displayName = "DesktopModalView";

export default memo(DesktopModalView);
